// ============================================================
// utils/httpClient.ts
// Cliente HTTP centralizado con:
//   • Inyección automática del Authorization header
//   • Refresco silencioso del access token antes de expirar
//   • Cola de peticiones en espera durante el refresh
//   • Logout automático si el refresh falla (token inválido)
//   • Reintentos automáticos de las peticiones que fallaron por 401
// ============================================================

import { tokenStorage } from './tokenStorage';

// URL base del backend — configurar en .env: REACT_APP_API_URL=http://host/api/v1
const API_BASE = process.env.REACT_APP_API_URL ?? '';

// Tipo de cliente — el backend lo usa para diferenciar sesiones WEB vs APK
// y aplicar políticas distintas (ej: una sesión WEB activa por usuario).
const CLIENT_TYPE = 'WEB';

/**
 * Razones que el backend devuelve en el header `X-Reason` cuando responde 401.
 * - session-replaced: otro dispositivo inició sesión y desplazó a éste.
 * - token-expired:    access token caducó — se debe intentar refresh.
 * - invalid-token:    token no válido / sesión cerrada en backend.
 */
export type LogoutReason = 'session-replaced' | 'invalid-token' | 'session-lost';

// ── Error con status HTTP ─────────────────────────────────────

export class HttpError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
    this.name = 'HttpError';
  }
}

// ── Convierte errores de red en mensajes comprensibles ─────────

function humanizeNetworkError(err: unknown): never {
  if (err instanceof DOMException && err.name === 'AbortError') {
    throw new Error('La solicitud fue cancelada.');
  }
  if (err instanceof TypeError) {
    // "Failed to fetch", "Load failed" (Safari), "NetworkError when attempting to fetch resource" (Firefox)
    throw new Error(
      'No se pudo conectar con el servidor. ' +
      'Verifica tu conexión a internet e inténtalo nuevamente.',
    );
  }
  throw err;
}

// ── Tipos ─────────────────────────────────────────────────────

export interface RefreshResponse {
  success: boolean;
  message: string;
  data: {
    token:        string;   // nuevo access token
    refreshToken: string;   // nuevo refresh token (rotación)
    expiracionMs: number;
  } | null;
}

// ── Estado interno del cliente ────────────────────────────────

/** Bandera que evita múltiples refresh simultáneos */
let isRefreshing = false;

/**
 * Cola de callbacks que esperan el nuevo access token.
 * Cuando el refresh termina, se resuelven o rechazan todos.
 */
let refreshQueue: Array<{
  resolve: (token: string) => void;
  reject:  (err: unknown)  => void;
}> = [];

/** Callback que se llama cuando el refresh falla → cierra sesión */
let onForceLogout: ((reason?: LogoutReason) => void) | null = null;

export const httpClient = {

  // ── Configuración ─────────────────────────────────────────

  setLogoutCallback(cb: (reason?: LogoutReason) => void): void {
    onForceLogout = cb;
  },

  /**
   * Permite que otros módulos (ej: SSE de notificaciones) disparen el logout
   * forzado con una razón específica. Útil cuando llega el evento
   * "session-replaced" antes que ningún 401 del backend.
   */
  forceLogout(reason: LogoutReason): void {
    tokenStorage.clearAll();
    onForceLogout?.(reason);
  },

  // ── Método principal ──────────────────────────────────────

  async request<T = unknown>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    // 1. ¿El access token está por expirar? → refresca antes de enviar
    if (tokenStorage.isAccessTokenExpiringSoon()) {
      await this._refreshAccessToken();
    }

    const response = await this._fetchWithAuth(endpoint, options).catch(humanizeNetworkError);

    // 2. Si el backend devuelve 401 → leer X-Reason para decidir
    if (response.status === 401) {
      const reason = (response.headers.get('X-Reason') ?? '').toLowerCase();

      // Caso 1: Otro dispositivo desplazó esta sesión → no intentar refresh.
      if (reason === 'session-replaced') {
        tokenStorage.clearAll();
        onForceLogout?.('session-replaced');
        throw new HttpError(
          'Tu sesión fue cerrada porque iniciaste sesión en otro dispositivo.',
          401,
        );
      }

      // Caso 2: Token inválido/desconocido → cerrar sin intentar refresh.
      if (reason === 'invalid-token') {
        tokenStorage.clearAll();
        onForceLogout?.('invalid-token');
        throw new HttpError('Sesión no válida. Inicia sesión nuevamente.', 401);
      }

      // Caso 3 (por defecto, incluye 'token-expired'): intentar refresh una vez.
      try {
        await this._refreshAccessToken();
        const retryResponse = await this._fetchWithAuth(endpoint, options).catch(humanizeNetworkError);
        return this._parseResponse<T>(retryResponse);
      } catch {
        // _refreshAccessToken ya hizo clearAll + onForceLogout en su catch.
        throw new Error('Sesión expirada. Por favor inicie sesión nuevamente.');
      }
    }

    return this._parseResponse<T>(response);
  },

  // ── Helpers de fetch ──────────────────────────────────────

  async _fetchWithAuth(endpoint: string, options: RequestInit): Promise<Response> {
    const accessToken = tokenStorage.getAccessToken();
    const headers = new Headers(options.headers);
    // Multipart: dejar que el navegador fije Content-Type con su boundary.
    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
    if (!isFormData && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }
    headers.set('X-Client-Type', CLIENT_TYPE);
    return fetch(`${API_BASE}${endpoint}`, { ...options, headers });
  },

  async _parseResponse<T>(response: Response): Promise<T> {
    const body = await response.json().catch(() => ({}));
    if (!response.ok || body.success === false) {
      // 403: token válido pero sin permiso para la operación
      const message = response.status === 403
        ? (body.message ?? 'No tienes permiso para realizar esta acción.')
        : (body.message ?? `Error ${response.status}`);
      throw new HttpError(message, response.status);
    }
    return (body.data ?? body) as T;
  },

  // ── Lógica de refresh ─────────────────────────────────────

  async _refreshAccessToken(): Promise<string> {
    // Si ya hay un refresh en curso → encolar y esperar
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      });
    }

    isRefreshing = true;

    try {
      const refreshToken = tokenStorage.getRefreshToken();
      if (!refreshToken) throw new Error('No hay refresh token');

      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type':  'application/json',
          'X-Client-Type': CLIENT_TYPE,
        },
        body: JSON.stringify({ refreshToken }),
      }).catch(humanizeNetworkError);

      const body: RefreshResponse = await res.json();

      if (!body.success || !body.data) {
        throw new Error(body.message ?? 'Refresh inválido');
      }

      const { token, refreshToken: newRefreshToken, expiracionMs } = body.data;

      // Guardar nuevos tokens (rotación)
      tokenStorage.saveAccessToken(token, expiracionMs);
      tokenStorage.saveRefreshToken(newRefreshToken);

      // Resolver todos los que estaban esperando
      refreshQueue.forEach(cb => cb.resolve(token));
      return token;

    } catch (err) {
      // Refresh fallido → cerrar sesión.
      // Cuando viene del path de refresh, el backend NO emite X-Reason: session-replaced
      // (viene de otro filter). Lo marcamos como 'session-lost' para que la UI muestre
      // un mensaje genérico de "sesión perdida — inicia sesión nuevamente".
      refreshQueue.forEach(cb => cb.reject(err));
      tokenStorage.clearAll();
      onForceLogout?.('session-lost');
      throw err;

    } finally {
      isRefreshing = false;
      refreshQueue = [];
    }
  },

  // ── Métodos de conveniencia ───────────────────────────────

  get<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  },

  post<T>(endpoint: string, body: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  put<T>(endpoint: string, body: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  patch<T>(endpoint: string, body: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  delete<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  },

  /** Sube archivos como multipart/form-data. El navegador fija Content-Type con boundary. */
  upload<T>(endpoint: string, formData: FormData, options?: Omit<RequestInit, 'body'>): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: options?.method ?? 'POST',
      body: formData,
    });
  },

  /**
   * Descarga un recurso protegido y retorna un `objectURL` (blob:) para usar en <img src>.
   * El llamador es responsable de invocar URL.revokeObjectURL cuando ya no lo necesite.
   */
  async fetchBlobUrl(endpoint: string): Promise<string> {
    if (tokenStorage.isAccessTokenExpiringSoon()) {
      await this._refreshAccessToken();
    }
    let response = await this._fetchWithAuth(endpoint, { method: 'GET' });
    if (response.status === 401) {
      await this._refreshAccessToken();
      response = await this._fetchWithAuth(endpoint, { method: 'GET' });
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new HttpError(body.message ?? `Error ${response.status}`, response.status);
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  },

  /** Descarga un archivo autenticado y lo guarda en el navegador. */
  async downloadFile(endpoint: string, defaultFilename = 'archivo'): Promise<void> {
    if (tokenStorage.isAccessTokenExpiringSoon()) {
      await this._refreshAccessToken();
    }
    const response = await this._fetchWithAuth(endpoint, { method: 'GET' }).catch(humanizeNetworkError);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new HttpError(body.message ?? `Error ${response.status}`, response.status);
    }
    const disposition = response.headers.get('Content-Disposition') ?? '';
    const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    const filename = match ? match[1].replace(/['"]/g, '') : defaultFilename;
    const blob = await response.blob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  },
};
